import { Button, CompanyLogo, Container, LanguageSwitcher } from '@/components/atoms';
import Avatar from '@/images/avatar/avatar.png';
import { ACTION_AUTH } from '@/redux/actions';
import PATH from '@/routes/path';
import { setDefaultOptions } from 'date-fns';
import { enUS, id } from 'date-fns/locale';
import i18n from 'i18next';

import { HELPERS } from '@/utils';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiMenu } from 'react-icons/bi';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

const locales = { en: enUS, id };

const HeaderMobile = ({ loggedIn, authProfile, links }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const history = useHistory();
  const { t } = useTranslation();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleClickLink = (link, e) => {
    setIsOpen(false);

    if (link.to === PATH.LEARNING_PROGRAM) {
      HELPERS.sendEventGA('click_learning_program_menu', { page_source: 'Header' });
    } else if ([PATH.CATALOG, PATH.CATALOG_COURSE].includes(link.to)) {
      HELPERS.sendEventGA('click_explore_courses_menu', { page_source: 'Header' });
    }
  };

  const handleOutsideClick = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };
  const dispatch = useDispatch();

  const [activeLanguage, setActiveLanguage] = useState(i18n.language);

  const handleSwitchLanguage = (lang) => {
    setDefaultOptions({ locale: locales[lang] });
    i18n.changeLanguage(lang);
  };

  const handleLogout = () => {
    dispatch(ACTION_AUTH.logout());
    HELPERS.GA.reset();
    history.push(PATH.HOME);
  };

  useEffect(() => setActiveLanguage(i18n.language), [i18n.language]);
  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);
  return (
    <div className="relative flex items-center h-full md:hidden" ref={menuRef}>
      <Container>
        <div className="flex items-center justify-between">
          <div className="flex items-center lg:hidden">
            <button
              type="button"
              className="flex lg:hidden items-center text-gray-800 focus:outline-none"
              onClick={toggleMenu}
              aria-label="Toggle Menu"
            >
              <BiMenu className="w-6 h-6" />
            </button>
            {isOpen && (
              <div className="absolute lg:hidden top-full left-0 w-full bg-white">
                <nav>
                  <ul className="flex flex-col space-y-2 p-4">
                    {links.map((item) => (
                      <li key={item.to}>
                        <Link
                          type="button"
                          onClick={(e) => handleClickLink(item, e)}
                          to={item.to}
                          target={item.target ?? '_self'}
                          className="flex lg:px-3 py-2 text-sm text-gray-600 hover:text-indigo-600 focus-visible:ring-2 focus-visible:ring-offset-2 transition focus-visible:ring-indigo-500 focus-visible:outline-none focus-visible:shadow-outline-indigo rounded-full"
                        >
                          {t(item.text)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
                {loggedIn && (
                  <>
                    <div className="flex items-center gap-2 p-4">
                      <img
                        className="object-cover object-top rounded-full w-11 h-11"
                        src={authProfile?.avatar_url || Avatar}
                        alt="Avatar"
                      />
                      <div>
                        <div className="font-semibold">{authProfile?.name}</div>
                        <div className="font-light text-gray-600">{authProfile?.email}</div>
                      </div>
                    </div>
                    <hr />
                    <Link
                      className="inline-block w-full px-4 py-3 hover:bg-gray-100"
                      to={PATH.PROFILE}
                      onClick={(e) => setIsOpen(false)}
                    >
                      {t('see_profile')}
                    </Link>
                  </>
                )}
                <hr />
                <div className="px-4 py-3 space-y-3">
                  <div>Bahasa</div>
                  <div className="space-y-2">
                    {LanguageSwitcher.languageOptions.map(({ value, label, description }) => (
                      <div key={value} className="flex items-center gap-1.5" htmlFor={`#${label}`}>
                        <input
                          checked={activeLanguage === value}
                          id={label}
                          type="radio"
                          name="language"
                          value={value}
                          className="w-4 h-4 ring-secondary"
                          onChange={() => handleSwitchLanguage(value)}
                        />
                        <label className="flex items-center gap-1 cursor-pointer" htmlFor={label}>
                          {description}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                {loggedIn && (
                  <>
                    <hr />
                    <button
                      className="inline-block w-full px-4 py-3 text-left hover:bg-gray-100"
                      type="button"
                      onClick={handleLogout}
                    >
                      {t('auth_logout')}
                    </button>
                  </>
                )}
                {!loggedIn && (
                  <>
                    <hr />
                    <div className="p-4 space-y-3">
                      <Button
                        variant={Button.VARIANTS.GRAY_OUTLINE}
                        className="w-full"
                        onClick={(e) => {
                          setIsOpen(false);

                          HELPERS.sendEventGA('click_login_menu', {
                            page_source: 'Header',
                          });

                          history.push(PATH.AUTH_LOGIN);
                        }}
                      >
                        {t('auth_login')}
                      </Button>
                      <Button
                        variant={Button.VARIANTS.SECONDARY}
                        className="w-full"
                        onClick={(e) => {
                          setIsOpen(false);

                          HELPERS.sendEventGA('click_register_menu', {
                            page_source: 'Header',
                          });

                          history.push(PATH.AUTH_REGISTER);
                        }}
                      >
                        {t('auth_register')}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex-grow" />
          <CompanyLogo link className="mx-auto" />
          <div className="flex-grow" />
        </div>
      </Container>
    </div>
  );
};

export default HeaderMobile;
